import { PropType, Component } from 'vue';
import { Model, Request } from '@viddsee/server-api-client';

import { browseApi } from '@/scripts/webcall/v3/browse';
import { tagsApi } from '@/scripts/webcall/v3/tags';

import { Nullable } from '@/scripts/models/utility-types';
import urlLocationHelper from '@/scripts/helpers/url-location-helper';

import { BrowseLikedComponent } from '@/scripts/Components/mixins/browseLikedComponent';
import ServerSidePropNormalizer from '@/scripts/Components/Utilities/ServerSidePropNormalizer';
import WaypointHandler from '@/scripts/Components/libs/waypoint-handler';

export enum PageType {
  Genre = 'genre',
  Topic = 'topic',
  Country = 'country',
  Whatsnew = 'whatsnew',
  Popular = 'popular',
}

enum SortOption {
  Latest = 'latest',
  Popular = 'popular',
}

interface ComponentData {
  isLoading: boolean;
  videos: Model.Video.Info[];
  // Waypoint related
  waypoint: Nullable<WaypointHandler>;
  hasErrorLoading: boolean;
  loadedPage: number;
  hasMorePages: boolean;
  // App specific variables
  shouldKeepSsrVideos: boolean;
  isMobileNavOpen: boolean;
  pageType: PageType;
}

export default {
  init(): Component {
    return BrowseLikedComponent.extend({
      components: {
        ServerSidePropNormalizer,
      },

      props: {
        name: {
          type: String,
          required: true,
        },
        type: {
          type: String as PropType<PageType>,
          required: true,
        },
        sort: {
          type: String as PropType<SortOption>,
          required: true,
        },
      },

      data(): ComponentData {
        return {
          isLoading: false,
          videos: [],
          // Waypoint related
          waypoint: null,
          hasErrorLoading: false,
          loadedPage: 0,
          hasMorePages: true,
          // App specific variables
          shouldKeepSsrVideos: true,
          isMobileNavOpen: false,
          pageType: this.type,
        };
      },
      watch: {
        sort(): void {
          this.updatePageType();
          this.videos.splice(0, this.videos.length);
          this.loadedPage = -1;
          this.waypoint && this.waypoint.destroy();
          this.loadMoreVideos();
          this.shouldKeepSsrVideos = false;
        },
      },
      mounted() {
        this.setUpWaypoint();
      },
      methods: {
        updatePageType(): void {
          const pathname = window.location.pathname;

          // TODO: Inconsistent naming here, latest is represented by "new", "whatsnew", "latest"
          if (pathname === '/new' || pathname === '/popular') {
            urlLocationHelper.path.update(this.sort === SortOption.Latest ? 'new' : 'popular');

            this.pageType = (this.sort === SortOption.Latest)
              ? PageType.Whatsnew
              : PageType.Popular
            ;
            return;
          }

          urlLocationHelper.query.add({
            sort: this.sort,
          });
        },

        async loadVideos(): Promise<Model.Pagination.PaginatedItemsResponse<Model.Video.Info>> {
          const tagName = (this.name || '').toLowerCase();
          const options: Request.Tags.VideosPaginationOptions = {
            page: this.loadedPage + 1,
            perPage: 12,
            type: this.sort,
          };

          const response = await Promise.resolve()
            .then(() => {
              switch (this.pageType) {
                case PageType.Genre:
                  return tagsApi.listGenreVideos({
                    genreSlug: tagName,
                  }, options);
                case PageType.Topic:
                  return tagsApi.listTopicVideos({
                    topicSlug: tagName,
                  }, options);
                case PageType.Country:
                  return tagsApi.listCountryVideos({
                    countrySlug: tagName,
                  }, options);
                case PageType.Popular:
                  return browseApi.listPopular(undefined, options);
                case PageType.Whatsnew:
                default:
                  return browseApi.listLatest(undefined, options);
              }
            });

          return response.data;
        },
      },
    });
  },
};
